import * as THREE from 'three';
import { tracerVert, tracerFrag } from './shaders/tracer.glsl.js';
import { CELL } from './Particles.js';

/**
 * Tracer rounds.
 *
 * Roughly every third round is a tracer, which is how belts and magazines are
 * actually loaded — a tracer on every shot turns a firefight into a laser show and
 * destroys the sense that bullets are objects rather than hitscans.
 *
 * Each tracer is a single GPU-animated segment: the head leaves the muzzle at a
 * plausible velocity and reaches the impact point over two or three frames. It is
 * *not* a line drawn instantly from A to B — that reads as a beam. The travel time
 * is the whole point.
 */

export const TUNING = {
  capacity: 40,
  speed: 460, // m/s; slower than a real round so the streak is legible
  tail: 7.0, // metres of visible trail behind the head
  width: 0.05,
  minPixels: 1.6,
  fraction: 0.34, // 1 in 3 rounds
  vapour: true,
};

const STRIDES = { tOrigin: 3, tDir: 3, tParams: 4, tCol: 4 };

export class Tracers {
  constructor(ctx, root, particles) {
    this.ctx = ctx;
    this.particles = particles;
    this.time = 0;
    this.capacity = TUNING.capacity;
    this.head = 0;
    this.shotCounter = 0;

    this.uniforms = {
      uTime: { value: 0 },
      uPxScale: { value: 0.002 },
      uMinPixels: { value: TUNING.minPixels },
    };

    this.material = new THREE.ShaderMaterial({
      vertexShader: tracerVert,
      fragmentShader: tracerFrag,
      uniforms: this.uniforms,
      transparent: true,
      depthWrite: false,
      depthTest: true,
      blending: THREE.CustomBlending,
      blendSrc: THREE.OneFactor,
      blendDst: THREE.OneFactor,
      side: THREE.DoubleSide,
    });

    const geo = new THREE.InstancedBufferGeometry();
    geo.setAttribute('position', new THREE.Float32BufferAttribute([0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0], 3));
    geo.setAttribute('uv', new THREE.Float32BufferAttribute([0, 0, 1, 0, 1, 1, 0, 1], 2));
    geo.setIndex([0, 1, 2, 0, 2, 3]);
    geo.instanceCount = 0;
    geo.boundingSphere = new THREE.Sphere(new THREE.Vector3(), 1e6);

    this.arrays = {};
    this.attrs = {};
    for (const name in STRIDES) {
      const arr = new Float32Array(this.capacity * STRIDES[name]);
      const attr = new THREE.InstancedBufferAttribute(arr, STRIDES[name]);
      attr.setUsage(THREE.DynamicDrawUsage);
      geo.setAttribute(name, attr);
      this.arrays[name] = arr;
      this.attrs[name] = attr;
    }

    this.geometry = geo;
    this.mesh = new THREE.Mesh(geo, this.material);
    this.mesh.frustumCulled = false;
    this.mesh.renderOrder = 12;
    this.mesh.matrixAutoUpdate = false;
    root.add(this.mesh);

    this._lo = -1;
    this._hi = -1;
    this._dx = 0; this._dy = 0; this._dz = 0;
  }

  /** True roughly one shot in three, deterministic enough to feel like a belt. */
  shouldTrace() {
    this.shotCounter++;
    return this.shotCounter % 3 === 0;
  }

  /**
   * Fire a tracer from a muzzle point to an impact point.
   * @param {number} r,g,b linear colour of the burning composition
   */
  fire(ox, oy, oz, tx, ty, tz, r = 1.0, g = 0.62, b = 0.22, width = TUNING.width) {
    let dx = tx - ox;
    let dy = ty - oy;
    let dz = tz - oz;
    const dist = Math.hypot(dx, dy, dz);
    if (dist < 0.05) return;
    dx /= dist; dy /= dist; dz /= dist;

    const i = this.head;
    this.head = (this.head + 1) % this.capacity;

    const A = this.arrays;
    let o = i * 3;
    A.tOrigin[o] = ox; A.tOrigin[o + 1] = oy; A.tOrigin[o + 2] = oz;
    A.tDir[o] = dx; A.tDir[o + 1] = dy; A.tDir[o + 2] = dz;
    o = i * 4;
    A.tParams[o] = TUNING.speed;
    A.tParams[o + 1] = this.time;
    A.tParams[o + 2] = dist;
    A.tParams[o + 3] = width;
    A.tCol[o] = r; A.tCol[o + 1] = g; A.tCol[o + 2] = b;
    A.tCol[o + 3] = Math.min(TUNING.tail, dist * 0.85);

    if (this._lo < 0 || i < this._lo) this._lo = i;
    if (i > this._hi) this._hi = i;
    if (this.geometry.instanceCount < this.capacity) this.geometry.instanceCount = Math.max(this.geometry.instanceCount, i + 1);

    /* ---- vapour trail: a couple of faint wisps left in the round's wake ---- */
    if (TUNING.vapour && this.particles && dist > 4) {
      const p = this.particles;
      for (let k = 0; k < 2; k++) {
        const f = dist * (0.3 + k * 0.35);
        p.beginAdd()
          .pos(ox + dx * f, oy + dy * f, oz + dz * f)
          .vel(dx * 1.2, dy * 1.2 + 0.25, dz * 1.2)
          .life(0.22 + Math.random() * 0.1)
          .drag(5)
          .grav(-0.05)
          .size(0.05, 0.5)
          .roll(Math.random() * 6.28, 0)
          .col(0.30, 0.30, 0.34, 0.22)
          .atlas(CELL.WISP)
          .fade(0.05, 1.6)
          .go();
      }
    }
  }

  update(dt, elapsed) {
    this.time = elapsed;
    this.uniforms.uTime.value = elapsed;
    // World units per pixel at one metre of depth, so the shader can enforce a
    // minimum on-screen thickness at any range or FOV.
    const cam = this.ctx.camera;
    this.uniforms.uPxScale.value = (2 * Math.tan((cam.fov * Math.PI) / 360)) / window.innerHeight;

    if (this._lo < 0) return;
    const count = this._hi - this._lo + 1;
    for (const name in STRIDES) {
      const s = STRIDES[name];
      const attr = this.attrs[name];
      attr.addUpdateRange(this._lo * s, count * s);
      attr.needsUpdate = true;
    }
    this._lo = this._hi = -1;
    void dt;
  }

  dispose() {
    this.geometry.dispose();
    this.material.dispose();
  }
}
